// pages/UserManagement.js
import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import UserManagementAPI from "../../services/API/staffUser";
import { useNotification } from "../../context/NotificationContext";
import ConfirmDialog from "../../components/ConfirmDialog";

export default function AddUserManagement() {
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  const navigate = useNavigate();
  const sessionExpiredRef = useRef(false);

  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [editingUser, setEditingUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // 👁 state
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // ✅ Fetch users
  const fetchUsers = useCallback(async () => {
    if (sessionExpiredRef.current) return;
    try {
      setIsLoading(true);
      const res = await UserManagementAPI.getUsers();
      setUsers(res);
    } catch (err) {
      console.error("Error fetching users", err);
      if (err?.response?.status === 401) {
        showNotificationRef.current(
          "Session expired. Please login again.",
          "error"
        );
        localStorage.removeItem("token");
        sessionExpiredRef.current = true;
        navigate("/login", { replace: true });
        return;
      }
      showNotificationRef.current(" Failed to load  User staffs", "error");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ✅ Handle input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ Submit new user
  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedPassword = formData.password.trim();

    if (!editingUser && trimmedPassword === "") {
      showNotification("Password cannot be empty or just spaces", "error");
      return;
    }
    if (editingUser && formData.password !== "" && trimmedPassword === "") {
      showNotification("Password cannot be only spaces", "error");
      return;
    }
    try {
      setIsLoading(true);
      if (editingUser) {
        await UserManagementAPI.updateUser(editingUser._id, {
          ...formData,
          password: trimmedPassword,
        });
        showNotification("User updated successfully", "success");
      } else {
        await UserManagementAPI.addUser({
          ...formData,
          password: trimmedPassword,
        });
        showNotification("User added successfully", "success");
      }
      setFormData({ name: "", email: "", password: "" });
      setEditingUser(null);
      fetchUsers(); // refresh table
    } catch (err) {
      console.error("Error adding/updating user", err);
      showNotification("Error adding/updating user", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (userid) => {
    setDeleteId(userid);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    try {
      setIsLoading(true);
      await UserManagementAPI.deleteUser(deleteId);
      showNotification(" User deleted successfully", "success");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user", error);
      showNotification(" Failed to delete user", "error");
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
      setIsLoading(false);
    }
  };

  // ✅ Edit user
  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: user.passwordorg, // keep blank for security, only update if admin types new
    });
  };

  // ✅ Cancel edit
  const handleCancelEdit = () => {
    setEditingUser(null);
    setFormData({ name: "", email: "", password: "" });
  };

  return (
    <div className="user-management-container">
      <h2 className="page-title">User Management</h2>

      {/* Form Section */}
      <div className="form-section">
        <h3>{editingUser ? "Edit User" : "Add User"}</h3>
        <form onSubmit={handleSubmit} className="user-form">
          <div className="form-group">
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              autoComplete="off"
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>
          <div className="form-group">
            <input
              type="email"
              name="email"
              placeholder="Email"
              autoComplete="off"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          {/* ------------------------ */}
          <div
            className="form-group password-group d-flex flex-row align-items-center"
            style={{
              border: "1px solid #ccc",
              borderRadius: "6px",
              padding: "0px 5px",
            }}
          >
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              autoComplete="off"
              placeholder={
                editingUser
                  ? "New Password (leave blank to keep current)"
                  : "Password"
              }
              value={formData.password}
              onChange={handleChange}
              required={!editingUser}
              className="form-input flex-grow-1"
              style={{
                border: "none",
                outline: "none",
                background: "transparent",
              }}
            />
            <span
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                cursor: "pointer",
                color: "#666",
                marginLeft: "8px",
              }}
            >
              {showPassword ? (
                <i className="fas fa-eye"></i> // 👁️ open eye
              ) : (
                <i className="fas fa-eye-slash"></i> // 🙈 closed eye
              )}
            </span>
          </div>

          {/* ----------------------------------------- */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading
                ? "Processing..."
                : editingUser
                ? "Update User"
                : "Add User"}
            </button>
            {editingUser && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Users Grid */}
      <div className="users-grid-section">
        <h3>User List</h3>
        {isLoading && users.length === 0 ? (
          <div className="loading">Loading users...</div>
        ) : (
          <div className="users-grid">
            {users.length > 0 ? (
              users
                .filter((user) => !user.isAdmin) // ✅ skip admins
                .map((user) => (
                  <div key={user._id} className="user-card">
                    <div className="user-info">
                      <h4 className="user-name">{user.name}</h4>
                      <p className="user-email">{user.email}</p>
                      <p className="user-password">{user.passwordorg}</p>
                      <div className="user-admin">
                        {user.isAdmin ? (
                          <span className="admin-badge">✅ Admin</span>
                        ) : (
                          <span className="user-badge">👤 User</span>
                        )}
                      </div>
                    </div>
                    <div className="user-actions">
                      <button
                        onClick={() => handleEdit(user)}
                        className="btn-action btn-edit"
                        disabled={isLoading}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user._id)}
                        className="btn-action btn-delete"
                        disabled={isLoading}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
            ) : (
              <div className="no-users">No users found</div>
            )}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Order?"
        message="Are you sure you want to delete this order? This action cannot be undone."
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
      <style jsx>{`
        .user-management-container {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
          font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          min-height: 0;
        }

        .page-title {
          color: #2c3e50;
          margin-bottom: 2rem;
          font-weight: 600;
          border-bottom: 2px solid #eee;
          padding-bottom: 0.5rem;
        }

        .form-section {
          background: white;
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .form-section h3 {
          margin-top: 0;
          color: #3498db;
        }

        .user-form {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 1rem;
          align-items: end;
        }

        @media (max-width: 768px) {
          .user-management-container {
            padding: 1rem;
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
            margin-top: 270px;
            max-height: calc(100vh - 80px);
          }

          .user-form {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .user-management-container {
            padding: 0.75rem;
            margin-top: 250px;
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
          }
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-input {
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1rem;
        }

        .form-input:focus {
          outline: none;
          border-color: #3498db;
          box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
        }

        .form-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background-color: #3498db;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background-color: #2980b9;
        }

        .btn-secondary {
          background-color: #95a5a6;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background-color: #7f8c8d;
        }

        .users-grid-section h3 {
          color: #2c3e50;
          margin-bottom: 1rem;
        }

        .users-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .user-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .user-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
        }

        .user-info {
          margin-bottom: 1rem;
        }

        .user-name {
          margin: 0 0 0.5rem 0;
          color: #2c3e50;
          font-size: 1.2rem;
        }

        .user-email {
          margin: 0 0 1rem 0;
          color: #7f8c8d;
          word-break: break-all;
        }

        .admin-badge,
        .user-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .admin-badge {
          background-color: #e8f5e9;
          color: #2e7d32;
        }

        .user-badge {
          background-color: #e3f2fd;
          color: #1565c0;
        }

        .user-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn-action {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.2s;
        }

        .btn-action:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-edit {
          background-color: #f39c12;
          color: white;
        }

        .btn-edit:hover:not(:disabled) {
          background-color: #e67e22;
        }

        .btn-delete {
          background-color: #e74c3c;
          color: white;
        }

        .btn-delete:hover:not(:disabled) {
          background-color: #c0392b;
        }

        .loading,
        .no-users {
          text-align: center;
          padding: 2rem;
          color: #7f8c8d;
          font-style: italic;
        }
      `}</style>
    </div>
  );
}
